/*
  The /t tap route (P2: D3, REQ-044, REQ-044a, REQ-045).

  A chip on a slab opens `/t?u=…&c=…&m=…`. This view checks the three values' shape,
  POSTs them once to /public/api/v1/nfc/verify, and then does one of four things:

  - **Malformed or incomplete link, or a backend 400** → the neutral "not a valid tap link"
    page. No band, no danger tone, no Retry (N4): a truncated share or a typo is not a
    verdict, INVALID is reserved for one the backend actually issued, and retrying the
    same bad values cannot succeed. The shape check is client-side, so no call is made.
  - **Any other error** — network, 5xx, 429, 409, any other 4xx (ruling R1) — → "could not
    verify right now" with a Retry that re-fires the same call (REQ-044). A 409 is the
    backend's optimistic-lock race between two taps of one chip, which a retry resolves.
  - **UNKNOWN_CHIP or INVALID with no `gradedId`** (D12 rows 1–2) → the band on its own, no
    Retry, since the backend did answer and there is no report to show (REQ-044a). Any other
    verdict without a `gradedId` takes the Retry page like an unknown verdict (ruling R7): a
    success band bound to no card is worse than a retry.
  - **A verdict with a `gradedId`** → straight on to the report, replacing /t in history,
    with the verdict in router state only (REQ-045, D14b). GradeReportView (P4) reads it.

  The once-guard is a ref, and it is about re-renders: React 17's StrictMode double-invokes
  render-phase functions but not effects, so it would not double-fire this on its own.
*/

import { ReactNode, useEffect, useRef } from "react";
import { Info } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { DataTableError } from "components/DataTable";
import { VerdictBand } from "components/VerdictBand";
import { isNfcVerdict } from "lib/nfcVerdict";
import {
  NfcTapState,
  NfcVerdict,
  useVerifyNfcTapMutation,
} from "services/pcgPublic";

import { PAGE_HEADING, PageHeadingRule } from "./pageHeading";

const PANEL = "skin-surface rounded-2xl border border-pcg-border bg-pcg-panel p-4 sm:p-6";

const U_PATTERN = /^[0-9a-f]{14}$/i;
const C_PATTERN = /^[0-9a-f]{6}$/i;
const M_PATTERN = /^[0-9a-f]{16}$/i;

interface TapParams {
  u: string;
  c: string;
  m: string;
}

/* Null unless all three are present and well-formed. */
const readTap = (params: URLSearchParams): TapParams | null => {
  const u = params.get("u");
  const c = params.get("c");
  const m = params.get("m");
  if (
    u === null ||
    c === null ||
    m === null ||
    !U_PATTERN.test(u) ||
    !C_PATTERN.test(c) ||
    !M_PATTERN.test(m)
  ) {
    return null;
  }
  return { u, c, m };
};

const Page = ({ children }: { children: ReactNode }) => (
  <div className="flex flex-col gap-4">
    <div>
      <h1 className={`${PAGE_HEADING} text-2xl font-semibold text-pcg-text`}>
        Chip verification
      </h1>
      <PageHeadingRule />
    </div>
    <div className={PANEL}>{children}</div>
  </div>
);

const NotATapLink = () => (
  <Page>
    <div className="flex flex-col items-center gap-2 px-4 py-10 text-center">
      <Info aria-hidden="true" className="h-6 w-6 text-pcg-textMuted" />
      <p className="max-w-prose text-sm font-semibold text-pcg-text">
        This is not a valid tap link. Tap the chip on the slab to verify it.
      </p>
    </div>
  </Page>
);

/* D12 rows 1–2: the only verdicts that stand alone, with no card behind them. */
const STANDALONE_VERDICTS: readonly NfcVerdict[] = ["UNKNOWN_CHIP", "INVALID"];

const VerifyTapView = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verify, result] = useVerifyNfcTapMutation();

  const tap = readTap(searchParams);
  const tapKey = tap ? `${tap.u}|${tap.c}|${tap.m}` : null;

  const fired = useRef(false);
  useEffect(() => {
    if (tap && !fired.current) {
      fired.current = true;
      verify(tap);
    }
    // Keyed on the values, not the object readTap rebuilds every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tapKey, verify]);

  /* An unrecognised verdict string is not rendered and not forwarded — fail closed. */
  const verdict =
    result.data && isNfcVerdict(result.data.verdict) ? result.data.verdict : null;
  const gradedId = verdict ? result.data?.gradedId : undefined;

  useEffect(() => {
    if (verdict && gradedId) {
      const state: NfcTapState = { nfcVerdict: verdict };
      navigate(`/report/${encodeURIComponent(gradedId)}`, { replace: true, state });
    }
  }, [verdict, gradedId, navigate]);

  if (!tap) {
    return <NotATapLink />;
  }

  if (result.isError) {
    const { error } = result;
    // The backend's own shape check refused the values: a bad link, not an outage. A 400
    // whose body is not JSON (a proxy's HTML page) arrives as PARSING_ERROR, still a 400.
    if (
      error &&
      "status" in error &&
      (error.status === 400 ||
        (error.status === "PARSING_ERROR" && error.originalStatus === 400))
    ) {
      return <NotATapLink />;
    }
  }

  const standalone =
    verdict !== null && !gradedId && STANDALONE_VERDICTS.includes(verdict);

  if (
    result.isError ||
    (result.isSuccess && !verdict) ||
    (verdict && !gradedId && !standalone)
  ) {
    return (
      <Page>
        <DataTableError
          title="Could not verify right now"
          description="The chip could not be checked just now. Please try again."
          onRetry={() => verify(tap)}
          retryLabel="Retry"
        />
      </Page>
    );
  }

  if (verdict && standalone) {
    return (
      <Page>
        <VerdictBand verdict={verdict} />
      </Page>
    );
  }

  /* Uninitialised, in flight, or navigating on to the report. */
  return (
    <Page>
      <p role="status" className="py-6 text-center text-sm text-pcg-textMuted">
        Verifying tap…
      </p>
    </Page>
  );
};

export default VerifyTapView;
